<template>
	<div
		v-if="ready"
		class="container-fluid"
	>

		<div class="row center">
			<div class="col-lg-12">
				<h1 class="main-content-title">
					<div class="flex-start">
						<el-button
							@click="cancel()"
							icon="el-icon-back"
							size="mini"
							style="margin-right: 10px"
							circle
						/>
						<div class="truncate-title">
							{{ presentation.title }}
						</div>
					</div>
					<div class="main-content-title-buttons">
						<el-button
							@click="openPreview()"
							:disabled="isDisabledPreview"
							:type="isDisabledPreview ? 'info' : 'warning'"
							plain
						>
							{{ translations.labels.preview }}
						</el-button>
						<el-button
							v-if="!isTeamViewer && isAvailableDevice"
							@click="openAssignDialog()"
							type="primary"
							:disabled="isDisabledPreview"
							plain
						>
							{{ translations.labels.assignToDevice }}
						</el-button>
						<el-button
							@click="presentation.used.devices ? openSaveDialog() : ajaxSaveVersion()"
							:type="!isDisabledPreview ? 'success' : 'info'"
							:disabled="isDisabledPreview"
							plain
						>
							{{ presentation.used.devices ? translations.labels.savePublish : translations.labels.save }}
						</el-button>
					</div>
				</h1>
			</div>
		</div>

		<div class="row center">
			<div class="col-lg-12">
				<div class="box">

					<div class="box-head with-line">
						<div class="box-head-buttons">
							<div class="flex-start">
								<el-tooltip
									:content="translations.labels.chooseLayout"
									:disabled="isTeamViewer"
									class="slot"
									effect="dark"
									placement="top"
								>
									<el-button
										@click="openLayoutDialog()"
										:disabled="isTeamViewer"
										icon="el-icon-s-grid"
										size="small"
										plain
									>
										{{ translations.labels.layout }}
									</el-button>
								</el-tooltip>

								<el-tooltip
									:content="translations.labels.createCustomLayout"
									:disabled="isTeamViewer"
									class="slot"
									effect="dark"
									placement="top"
								>
									<el-button
										@click="addSlot()"
										:disabled="isTeamViewer"
										icon="el-icon-files"
										size="small"
										plain
									>
										{{ translations.labels.addSlot }}
									</el-button>
								</el-tooltip>
								<el-tooltip
									:content="translations.labels.chooseBackgroundColor"
									:disabled="isTeamViewer"
									class="slot"
									effect="dark"
									placement="top"
								>
									<el-color-picker
										v-model="version.bgColor"
										:disabled="isTeamViewer"
										class="margin-left-10"
										size="small"
									/>
								</el-tooltip>
							</div>
							<div
								v-if="versions.length > 0"
								class="table-header-filters"
							>
								<el-tooltip
									:content="translations.labels.chooseVersion"
									:disabled="isTeamViewer"
									class="slot"
									effect="dark"
									placement="top"
								>
									<el-select
										v-model="tempVersion"
										@change="changeVersion(tempVersion)"
										:disabled="isTeamViewer || (versions.length === 0 || processing.publish)"
										:placeholder="translations.labels.selectVersion"
										size="medium"
									>
										<el-option
											v-for="(item, index) in versions"
											:key="index"
											:label="translations.labels.screenVersion"
											:value="item.id"
											class="autoheight"
											:class="{ 'current': item.id === presentation.currentVersion }"
											style="padding: 10px;"
										>
											<div style="line-height: 1.3; font-size: 13px;">
												{{ convertDate(item.createdAt, 'dateTime') }}
											</div>
											<div class="flex-between" style="line-height: 1.3; font-size: 11px;">
												{{ item.createdBy }}
											</div>
											<span
												v-if="item.id === presentation.currentVersion"
												class="el-icon-circle-check"
											/>
										</el-option>
									</el-select>
								</el-tooltip>
							</div>
						</div>
					</div>

					<div class="box-body">
						<div
							class="screen"
							:style="{ 'aspectRatio': aspectRatio + '/1', 'backgroundColor': version.bgColor }"
							ref="screen"
						>
							<div class="grid">
								<div
									v-for="(vert, index) in parseInt(presentation.settings.columns)"
									:key="'vert-' + index"
									class="grid-line vertical"
									:style="{ 'left': calculateLeftPosition(index) }"
									:data-index="index"
								/>
								<div
									v-for="(horz, index) in parseInt(presentation.settings.rows)"
									:key="'horz-' + index"
									class="grid-line horizontal"
									:style="{ 'top': calculateTopPosition(index) }"
								/>
							</div>
							<fullscreen
								:fullscreen.sync="preview"
								class="frame"
								:class="{ 'in-preview': preview }"
							>
								<div :style="{
									'aspectRatio': !preview ? aspectRatio + '/1' : 'auto',
									'height': preview ? screenPreview.height : '100%',
									'width': preview ? screenPreview.width : '100%' ,
									'position' : 'relative'
								}">
									<Layout
										v-if="showLayout"
										:slots="version.slots"
										:preview="preview"
										:backgroundColor="version.bgColor"
										:settings="presentation.settings"
									/>
								</div>
							</fullscreen>
						</div>
					</div>

				</div>
			</div>
		</div>

		<!-- select layout -->
		<el-dialog
			:title="translations.labels.selectLayout"
			:visible.sync="dialog.layout"
			:show-close="false"
			:close-on-press-escape="false"
			:close-on-click-modal="false"
			width="800px"
		>
			<div class="margin-bottom-20">
				<el-alert
					:title="translations.messages.warningChooseSlot"
					:closable="false"
					type="error"
				/>
			</div>
			<div class="setup">
				<div
					v-for="(layout, index) in configLayout"
					:key="index"
					@click="selectLayout(layout.id)"
					class="setup-item"
					:class="[ {'selected': tempLayout == layout.id }, presentation.settings.orientation ]"
					:style="{ backgroundImage: `url(/images/layouts/${presentation.settings.orientation}/${layout.id}.png)` }"
				/>
			</div>
			<div class="align-right">
				<el-button
					@click="insertLayout()"
					:disabled="tempLayout === null"
					:type="tempLayout === null ? 'info' : 'primary'"
					plain
				>
					{{ translations.labels.useThisLayout }}
				</el-button>
				<el-button
					@click="closeLayoutDialog()"
					plain
				>
					{{ translations.labels.cancel }}
				</el-button>
			</div>
		</el-dialog>
		<!-- /select layout -->

		<!-- widget manager -->
		<el-dialog
			:title="translations.labels.slotManager"
			:visible.sync="dialog.widgets"
			@closed="closedWidgetManagerDialog"
			:show-close="false"
			:close-on-press-escape="false"
			:close-on-click-modal="false"
			width="660px"
		>
			<div class="margin-bottom-20">
				{{ translations.messages.warningAddWidgets }}
			</div>
			<div class="manager">
				<div class="manager-header">
					<div class="row">
						<div class="col-lg-6 align-center">
							{{ translations.labels.availableWidgets }}
						</div>
						<div class="col-lg-6 align-center">
							{{ translations.labels.selectedWidgets }}
						</div>
					</div>
				</div>
				<div class="manager-body">
					<div class="manager-body-list">
						<div
							v-for="(widget, index) in $config.widgets"
							:key="index"
							@click="insertWidget(widget.id)"
							class="manager-body-list-item"
						>
							<span class="el-icon-copy-document" />
							{{ widget.title }}
							<span class="el-icon-right" />
						</div>
					</div>
					<div class="manager-body-list">
						<Container @drop="onDrop">
							<Draggable
								v-for="(widget, index) in tempWidgetCollection"
								:key="index"
							>
								<div
									class="manager-body-list-item grab"
									:class="{ 'set': widget.setup }"
								>
									{{ widget.id }}
									<span :class="widget.setup ? 'el-icon-success' : 'el-icon-warning-outline'" />

									<el-button
										@click="openWidgetSettingsDialog(index, tempWidgetCollection.length)"
										class="manager-body-list-item-button edit"
										type="primary"
										icon="el-icon-edit-outline"
										size="mini"
										circle
										plain
									/>

									<el-popconfirm
										@confirm="deleteWidget(index)"
										:title="translations.messages.warningRemoveWidget"
										:cancel-button-text="translations.labels.noCancel"
										confirm-button-text="OK"
										hide-icon
									>
										<el-button
											class="manager-body-list-item-button delete"
											type="danger"
											icon="el-icon-delete"
											size="mini"
											slot="reference"
											circle
											plain
										/>
									</el-popconfirm>

								</div>
							</Draggable>
						</Container>
					</div>
				</div>
			</div>
			<div class="align-right">
				<el-button
					@click="saveWidgetsToSlot()"
					:disabled="tempWidgetCollection.length === 0"
					:type="tempWidgetCollection.length === 0 ? 'info' : 'primary'"
					plain
				>
					{{ translations.labels.saveChanges }}
				</el-button>
				<el-button
					@click="closeWidgetManagerDialog()"
					plain
				>
					{{ translations.labels.cancel }}
				</el-button>
			</div>
		</el-dialog>
		<!-- /widget manager -->

		<!-- edit widget -->
		<el-dialog
			:title="tempWidget ? `${translations.labels.edit} ${tempWidget.title}` : ''"
			:visible.sync="dialog.settings"
			@closed="closedWidgetSettingsDialog"
			:show-close="false"
			:close-on-press-escape="false"
			:close-on-click-modal="false"
			width="500px"
		>
			<WidgetSettings
				v-if="tempWidget"
				:widget="tempWidget"
				:widgetsAmount="tempLength"
			/>
		</el-dialog>
		<!-- /edit widget -->

		<!-- assign -->
		<AssignToDevice
			:current="presentation"
			:devices="devices"
			:processing="processing.assign"
			:show="dialog.assign"
			:title="translations.labels.assignPresentation"
			type="presentation"
			:warning="translations.messages.warningAssignPresentation"
			:warningApply="translations.messages.warningAppliedImmediately"
		/>
		<!-- /assign -->

		<!-- save confirmation -->
		<el-dialog
			:title="translations.labels.areYouSure"
			:visible.sync="dialog.save"
			:show-close="false"
			:close-on-press-escape="false"
			:close-on-click-modal="false"
			width="400px"
		>
			{{ translations.messages.warningSavePresentation }}
			<div slot="footer" class="dialog-footer">
				<el-button
					@click="ajaxSaveVersion()"
					:loading="processing.save"
					type="primary"
					plain
				>
					{{ translations.labels.savePublish }}
				</el-button>
				<el-button
					@click="closeSaveDialog()"
					plain
				>
					{{ translations.labels.cancel }}
				</el-button>
			</div>
		</el-dialog>
		<!-- /save confirmation -->

		<LeaveConfirmation />

		<portal-target name="mediaUpload" />

		<portal-target name="mediaLibrary" />

	</div>
</template>

<script>
import Vue from 'vue'
import axios from 'axios'
import { PortalTarget } from 'portal-vue'
import fullscreen from 'vue-fullscreen'
import { mapState } from 'vuex'

import { Container, Draggable } from 'vue-smooth-dnd'

import { applyDrag } from '@/services/utils/applyDrag'
import { EventBus } from '@/services/event-bus'

import convertDate from '@/services/mixins/convertDate'
import randomString from '@/services/mixins/randomString'
import sortArray from '@/services/mixins/sortArray'
import updateLeaveConfirmation from '@/services/mixins/updateLeaveConfirmation'

import { _getPresentation, _loadVersions, _saveVersion } from '@/services/api/presentations'
import { _getAllDevices, _assignToDevice } from '@/services/api/devices'
import { _updateUsedMedia } from '@/services/api/media'

Vue.use(fullscreen)

export default {
	name: 'PresentationsSetup',
	metaInfo () {
		return {
			title: this.translations.labels.setupPresentation
		}
	},
	components: {
		Container,
		Draggable,
		PortalTarget,
		Layout: () => import( /* webpackPrefetch: true */ '@/views/Private/Pages/Presentations/Layout'),
		LeaveConfirmation: () => import( /* webpackPrefetch: true */ '@/views/Private/Components/Dialog/Leave'),
		WidgetSettings: () => import( /* webpackPrefetch: true */ '@/views/Private/Components/Widgets/Settings'),
		AssignToDevice: () => import( /* webpackPrefetch: true */ '@/views/Private/Components/Dialog/AssignToDevice')
	},
	mixins: [
		convertDate,
		randomString,
		sortArray,
		updateLeaveConfirmation
	],
	data () {
		return {
			availableSpace: false,
			configLayout: null,
			confirm: '',
			devices: [],
			dialog: {
				layout: false,
				widgets: false,
				delete: false,
				remove: false,
				settings: false,
				assign: false,
				save: false
			},
			initial: true,
			presentation: {
				id: null,
				title: null,
				description: null,
				settings: {
					orientation: null,
					resolution: null,
					color: null
				},
				currentVersion: null
			},
			preview: false,
			processing: {
				update: false,
				publish: false,
				settings: false,
				assign: false,
				save: false,
			},
			changed: false,
			saved: false,
			screenPreview: {
				height: null,
				width: null
			},
			showLayout: true,
			tempAssign: {},
			tempLayout: null,
			tempLength: null,
			tempSlot: null,
			tempVersion: null,
			tempWidget: null,
			tempWidgetCollection: [],
			usedMedia: [],
			version: {
				id: null,
				assetsTotalSize: 0,
				bgColor: '#FFFFFF',
				layout: null,
				slots: []
			},
			versions: [],
			versionHash: null
		}
	},
	computed: {
		...mapState(['leaveConfirmation']),
		aspectRatio () {
			const res = this.presentation.settings.resolution.split('x')
			return parseInt(res[0]) / parseInt(res[1])
		},
		isDisabledPreview () {
			return this.version.slots.length === 0
		},
		isAvailableDevice () {
			return this.devices.some(item => item.settings.resolution === this.presentation.settings.resolution)
		}
	},
	watch: {
		version: {
			deep: true,
			handler() {
				if (!this.initial) {
					this.updateLeaveConfirmation(true, false, null)
					this.changed = true
				}
				this.initial = false
			}
		},
		'preview' () {
			this.screenPreview = {
				height: null,
				width: null
			}
			if (this.preview) {
				const res = this.presentation.settings.resolution.split('x')
				const width = parseInt(res[0])
				const height = parseInt(res[1])
				const windowWidth = screen.width
				const windowHeight = screen.height
				if (width >= height) {
					this.screenPreview = {
						height: `${windowWidth / (width / height)}px`,
						width: `${windowWidth}px`
					}

				} else {
					this.screenPreview = {
						height: `${windowHeight}px`,
						width: `${windowHeight / (height / width)}px`
					}
				}
			}
		}
	},
	created () {
		this.objectLength(this.userTeam) ? this.ajaxLoadData() : this.redirectTo('/team/fallback')
	},
	mounted () {
		EventBus.$on('update-static', (data) => this.updateStatic(data))
		EventBus.$on('open-widget', (data) => this.openWidgetManagerDialog(data))
		EventBus.$on('update-widget', (data) => this.updateWidget(data))
		EventBus.$on('close-settings', () => this.closeWidgetSettingsDialog())
		EventBus.$on('close-assign', () => this.closeAssignDialog())
		EventBus.$on('submit-assign', (data) => this.ajaxAssignPresentation(data))
	},
	destroyed () {
		EventBus.$off('update-static')
		EventBus.$off('open-widget')
		EventBus.$off('update-widget')
		EventBus.$off('close-settings')
		EventBus.$off('close-assign')
		EventBus.$off('submit-assign')
	},
	methods: {

		ajaxLoadData () {
			axios.all([
				_getPresentation(this.$route.params.id),
				_getAllDevices(),
			])
			.then(axios.spread((res1, res2) => {
				this.presentation = res1
				this.devices = res2
				this.configLayout = res1.settings.orientation === 'portrait' ? this.$config.layouts.portrait : this.$config.layouts.landscape
				this.ajaxLoadVersions()
				this.removeLoader()
			}))
			.catch(error => {
				const errorCode = error.response.data.status
				setTimeout (() => {
					this.displayMessage(this.translations.statusCodes[errorCode] ? this.translations.statusCodes[errorCode] : this.translations.statusCodes['default'], 'error')
				}, 200)
			})
		},
		ajaxLoadVersions () {
			_loadVersions(this.$route.params.id)
				.then(response => {
					this.versions = response
					if (this.presentation.currentVersion === null) {
						if (!this.isTeamViewer && !this.saved) {
							this.openLayoutDialog()
						}
					} else {
						const newVersion = this.versions.find(item => item.id === this.presentation.currentVersion)
						this.version = this.clone(newVersion)
						this.tempVersion = this.version.id
					}
				})
		},
		ajaxLoadDevices () {
			_getAllDevices()
				.then(response => {
					this.devices = response
				})
		},
		ajaxSaveVersion () {
			this.processing.save = true
			this.caluclateAssetsSize()
			const version = {
				bgColor: this.version.bgColor,
				slots: this.version.slots,
				layout: this.version.layout,
				assetsTotalSize: this.version.assetsTotalSize
			}
			_saveVersion(version, this.$route.params.id)
				.then(response => {

					this.saved = true
					this.ajaxUpdateUsedMedia(response.id)
					this.ajaxLoadVersions()
					this.updateLeaveConfirmation(false, false, null)
					this.changed = false

					const newID = response.id
					this.presentation.currentVersion = newID
					this.tempVersion = newID
					this.version.id = newID

					this.updateLeaveConfirmation(false, false, null)
					this.changed = false

					setTimeout (() => {
						this.closeSaveDialog()
						this.displayMessage(this.translations.messages.successSavePresentation, 'success')
					}, 200)

				})
				.catch(error => {
					const errorCode = error.response.data.status
					setTimeout (() => {
						this.closeSaveDialog()
						this.displayMessage(this.translations.statusCodes[errorCode] ? this.translations.statusCodes[errorCode] : this.translations.statusCodes['default'], 'error')
					}, 200)
				})
		},
		ajaxAssignPresentation (data) {
			this.processing.assign = true
			_assignToDevice(data)
				.then(() => {
					this.ajaxLoadData()
					setTimeout (() => {
						this.closeAssignDialog()
						this.displayMessage(this.translations.messages.successAssignPresentation, 'success')
						this.processing.assign = false
					}, 200)
				})
				.catch(error => {
					const errorCode = error.response.data.status
					setTimeout (() => {
						this.displayMessage(this.translations.statusCodes[errorCode] ? this.translations.statusCodes[errorCode] : this.translations.statusCodes['default'], 'error')
						this.processing.assign = false
					}, 200)
				})
		},
		ajaxUpdateUsedMedia (uuid) {
			_updateUsedMedia(uuid, this.usedMedia)
		},

		openLayoutDialog () {
			this.dialog.layout = true
		},
		closeLayoutDialog () {
			this.dialog.layout = false
		},
		selectLayout (id) {
			this.tempLayout = id
		},
		insertLayout () {
			this.version.layout = this.tempLayout
			this.tempLayout = null
			const layouts = this.clone(this.configLayout)
			const temp = this.version.layout === 0 ? [] : layouts.find(item => item.id  === this.version.layout).slots
			temp.forEach(item => {
				item.i = this.randomString()
				item.w = Math.round(item.w * this.presentation.settings.columns / 100)
				item.h = Math.round(item.h * this.presentation.settings.rows / 100)
				item.x = Math.round(item.x * this.presentation.settings.columns / 100)
				item.y = Math.round(item.y * this.presentation.settings.rows / 100)
				item.static = true
				item.widgets = []
			})
			this.version.slots = temp
			this.closeLayoutDialog()
			this.updateLeaveConfirmation(true, false, null)
			this.changed = true
		},
		openWidgetManagerDialog (id) {
			this.tempSlot = id
			const slot = this.clone(this.version.slots.find(item => item.i === id))
			this.tempWidgetCollection = slot.widgets.length > 0 ? slot.widgets : []
			this.dialog.widgets = true
		},
		closeWidgetManagerDialog () {
			this.dialog.widgets = false
		},
		closedWidgetManagerDialog () {
			this.tempSlot = null
			this.tempWidgetCollection = []
		},
		openWidgetSettingsDialog (index, length) {
			this.tempWidget = this.tempWidgetCollection[index]
			this.dialog.settings = true
			this.tempLength = length
		},
		closeWidgetSettingsDialog () {
			this.dialog.settings = false
		},
		closedWidgetSettingsDialog () {
			this.tempWidget = null
		},
		insertWidget (id) {
			let widget = this.clone(this.$config.widgets.find(item => item.id === id))
			widget.unique = this.randomString()
			widget.setup = false
			widget.settings = {}
			this.tempWidgetCollection.push(widget)
		},
		deleteWidget (index) {
			this.tempWidgetCollection.splice(index, 1)
		},
		updateWidget (data) {
			const index = this.tempWidgetCollection.findIndex(item => item.unique === data.unique)
			this.tempWidgetCollection[index].settings = data.settings
			this.tempWidgetCollection[index].setup = data.setup
			this.closeWidgetSettingsDialog()
		},
		saveWidgetsToSlot () {
			const index = this.version.slots.findIndex(item => item.i === this.tempSlot)
			this.version.slots[index].widgets = this.tempWidgetCollection
			this.closeWidgetManagerDialog()
		},
		addSlot () {
			const element = {
				i: this.randomString(),
				w: 10,
				h: 10,
				x: 0,
				y: 0,
				static: false,
				widgets: []
			}
			this.version.slots.push(element)
			this.updateLeaveConfirmation(true, false, null)
		},
		updateStatic (id) {
			const index = this.version.slots.findIndex(item => item.i == id)
			this.version.slots[index].static = !this.version.slots[index].static
		},
		openPreview () {
			this.preview = true
		},
		changeVersion (version) {
			const current = this.versions.filter(item => {
				return item.id === version
			})
			this.presentation.currentVersion = version
			this.version = {
				id: version,
				bgColor: current[0].bgColor,
				layout: current[0].layout,
				slots: current[0].slots
			}
			this.changed = false
		},
		cancel () {
			if (this.leaveConfirmation.changed) {
				this.updateLeaveConfirmation(true, true, '/presentations')
			} else {
				this.$router.go(-1)
			}
		},
		findWidgetVersion (id) {
			const widget = this.$config.widgets.find(item => item.id === id)
			return widget.version
		},
		calculateLeftPosition (index) {
			const percent = 100 / parseInt(this.presentation.settings.columns)
			return index * percent + '%'
		},
		calculateTopPosition (index) {
			const percent = 100 / parseInt(this.presentation.settings.rows)
			return index * percent + '%'
		},
		onDrop (dropResult) {
			this.tempWidgetCollection = applyDrag(this.tempWidgetCollection, dropResult)
		},
		caluclateAssetsSize () {
			this.version.assetsTotalSize = 0
			this.usedMedia = []
			this.version.slots.forEach(slot => {
				slot.widgets.forEach(widget => {
					if (widget.id === 'MEDIA') {
						if (
							this.objectLength(widget.settings)
							&& this.objectLength(widget.settings.media)
							&& widget.settings.media.size
						) {
							this.version.assetsTotalSize = this.version.assetsTotalSize + widget.settings.media.size
							this.usedMedia.push(widget.settings.media.id)
						}
					}
				})
			})
		},
		openAssignDialog () {
			this.dialog.assign = true
		},
		closeAssignDialog () {
			this.dialog.assign = false
			this.processing.assign = false
		},
		openSaveDialog () {
			this.dialog.save = true
			this.processing.save = false
		},
		closeSaveDialog () {
			this.dialog.save = false
			this.processing.save = false
		}
	}
}
</script>

<style lang="scss" scoped>

.screen {
	border: 8px solid darken($color-line, 50%);
	border-radius: 6px;
	@include flex(flex, row, wrap, center, center);
	position: relative;
}

.fullscreen {

	background-color: #000 !important;
	@include flex(flex, column, wrap, center, center);
	height: 100vh;
	overflow: hidden !important;
	width: 100vw;

	&-preview {

		height: auto !important;
		left: auto !important;
		margin: 0 !important;
		position: relative !important;
		top: auto !important;

		&.landscape {
			transform-origin: left center !important;
		}

		&:after {
			content: '';
			height: 100%;
			@include position($position: absolute, $top: 0, $left: 0);
			width: 100%;
			z-index: 999;
		}

	}

}

.frame {

	height: 100%;
	width: 100%;
	z-index: 1;

	&.portrait {
		left: auto;
		right: auto;
	}

}

.grid {

	height: 100%;
	width: 100%;
	@include position($position: absolute, $top: 0, $left: 0);
	z-index: 0;

	&-line {

		position: absolute;

		&.horizontal {
			border-bottom: 1px dashed darken($color-background, 8%);
			top: 0;
			width: 100%;
		}

		&.vertical {
			border-right: 1px dashed darken($color-background, 8%);
			height: 100%;
			left: 0;
		}

	}

}

.setup {

	display: grid;
	gap: 20px;
	grid-template-columns: repeat(4, 1fr);
	margin-bottom: 24px;

	@include breakpoint(sm) {
		grid-template-columns: repeat(3, 1fr);
	}

	@include breakpoint(xs) {
		grid-template-columns: repeat(2, 1fr);
	}

	&-item {

		aspect-ratio: 3/2;
		background-position: center;
		background-repeat: no-repeat;
		border-radius: 6px;
		border: 2px solid lighten($color-line, 6%);
		cursor: pointer;
		@include flex(flex, column, wrap, center, center);
		font-size: $font-size - 2;
		font-weight: $font-weight-medium;
		transition: border-color .2s ease;
		user-select: none;

		&.landscape {
			background-size: 80px 45px;
		}

		&.portrait {
			background-size: 40px 71px;
		}

		&:hover {

			border-color: darken($color-line, 15%);

			.setup-item-icon {
				background-color: darken($color-line, 15%);
			}

		}

		&.selected {

			border-color: $color-1;
			color: $color-1;

			.setup-item-icon {
				background-color: $color-1;
			}

		}

		&-icon {
			background-color: lighten($color-line, 6%);
			border-radius: 5px;
			color: #fff;
			@include flex(flex, row, wrap, center, center);
			font-size: 24px;
			height: 48px;
			margin-bottom: 8px;
			transition: background-color .2s ease;
			width: 48px;
		}

	}

}

.manager {

	border-bottom: 1px solid $color-line;
	margin-bottom: 24px;

	&-header {
		background-color: $color-background;
		border: 1px solid $color-line;
		border-radius: 4px;
		font-size: $font-size - 1;
		font-weight: $font-weight-medium;
		padding: 12px 0;
		text-transform: uppercase;
		user-select: none;
	}

	&-body {

		@include flex(flex, row, wrap, space-between, flex-start);
		height: 380px;
		position: relative;

		&:after {
			border-right: 1px solid $color-line;
			content: '';
			height: 100%;
			@include position($position: absolute, $top: 0, $left: 50%);
		}

		&-list {

			height: 380px;
			padding: 20px 10px 10px;
			overflow: hidden;
			overflow-y: auto;
			width: calc(50% - 10px);

			&-item {

				border: 1px solid $color-line;
				border-radius: 5px;
				@include flex(flex, row, wrap, flex-start, center);
				cursor: pointer;
				font-size: $font-size - 2;
				font-weight: $font-weight-medium;
				margin-bottom: 10px;
				padding: 13px 16px 13px 42px;
				position: relative;
				text-transform: uppercase;
				transition: border-color .2s ease;
				user-select: none;
				width: 100%;

				&:hover {
					border-color: $color-1;
				}

				&.set {
					border-color: $color-success;
				}

				&.grab {
					cursor: grab;
				}

				.el-icon {

					&-copy-document,
					&-right,
					&-success,
					&-warning-outline {
						font-size: 17px;
						position: absolute;
						top: 50%;
						transform: translateY(-50%);
					}

					&-right {
						right: 16px;
					}

					&-success,
					&-warning-outline,
					&-copy-document {
						left: 16px;
					}

					&-success {
						color: $color-success;
					}

					&-warning-outline {
						color: darken($color-line, 12%);
					}

				}

				&-button {

					position: absolute;
					top: 50%;
					transform: translateY(-50%);

					&.delete {
						right: 16px;
					}

					&.edit {
						right: 50px;
					}

				}

			}

		}

	}

}

</style>
